import {ChangeDetectionStrategy, Component, OnDestroy, OnInit} from '@angular/core';
import {TaxonomyFacadeService} from './taxonomy-facade.service';
import {Taxonomy, taxonomyMaxLevel} from './taxonomy.reducer';
import {FlatTreeControl} from '@angular/cdk/tree';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {SubSink} from 'subsink';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {
  SegmentAttachToTaxonomyModalComponent,
  SegmentAttachToTaxonomyModalData
} from './segment-attach-to-taxonomy-modal/segment-attach-to-taxonomy-modal.component';
import {TaxonomyEditComponent, TaxonomyEditData} from './taxonomy-edit/taxonomy-edit.component';
import {TaxonomyCreateComponent} from './taxonomy-create/taxonomy-create.component';
import {ActionsSubject} from '@ngrx/store';
import {BehaviorSubject, merge} from 'rxjs';
import {ofType} from '@ngrx/effects';
import {refreshTaxonomies} from './taxonomy.actions';
import {TaxonomyFilterModel} from './taxonomy-filters/taxonomy-filters.component';

export interface TaxonomyFlatNode extends Taxonomy {
  expandable: boolean;
}

@Component({
  selector: 'app-taxonomy',
  template: `
    <app-taxonomy-documentation></app-taxonomy-documentation>
    <div class="filters-wrapper">
      <h3>Filters</h3>
      <app-taxonomy-filters (filterChanged)="onFilterChange($event)"></app-taxonomy-filters>
    </div>
    <button mat-button mat-raised-button [color]="'primary'" (click)="openCreateModal()">Create new root level
      taxonomy
    </button>

    <mat-tree *ngIf="dataSource.data"
              [dataSource]="dataSource"
              [treeControl]="treeControl"
              class="taxonomy-tree">
      <mat-tree-node *matTreeNodeDef="let node"
                     matTreeNodeToggle
                     matTreeNodePadding
                     [ngClass]="visible(node) ? {active: true} : {}"
                     (mouseover)="showButtons(node)"
                     (mouseout)="hideButtons(node)">
        <b>{{node.name}}</b>
        <ng-container>
          <div class="buttons-container">
            <button mat-icon-button
                    [ngStyle]="{visibility: visible(node) ? 'visible' : 'hidden'}"
                    (click)="openEditModal(node)"
                    style="width: 100px">
              <mat-icon>{{'edit'}}</mat-icon>
              Edit
            </button>
            <button mat-icon-button
                    [ngStyle]="{visibility: visible(node) ? 'visible' : 'hidden'}"
                    (click)="openAttachSegmentsModal(node)"
                    style="width: 150px">
              <mat-icon>{{'add'}}</mat-icon>
              Attach segments
            </button>
          </div>
        </ng-container>
      </mat-tree-node>
      <!--  node on the last available level -->
      <mat-tree-node *matTreeNodeDef="let node; when: canHaveMoreChildren"
                     matTreeNodePadding
                     [ngClass]="visible(node) ? {active: true} : {}"
                     (mouseover)="showButtons(node)"
                     (mouseout)="hideButtons(node)">
        <b>{{node.name}}</b>
        <div class="buttons-container">
          <button mat-icon-button
                  matTreeNodeToggle
                  [attr.aria-label]="'Toggle ' + node.item">
            <mat-icon class="mat-icon-rtl-mirror">
              {{treeControl.isExpanded(node) ? 'expand_more' : 'chevron_right'}}
            </mat-icon>
          </button>
          <button mat-icon-button (click)="openCreateModal(node)" style="width: 100px">
            <mat-icon>add</mat-icon>
            Add new level
          </button>
          <ng-container>
            <button mat-icon-button
                    [ngStyle]="{visibility: visible(node) ? 'visible' : 'hidden'}"
                    (click)="openEditModal(node)"
                    style="width: 100px">
              <mat-icon>{{'edit'}}</mat-icon>
              Edit
            </button>
            <button mat-icon-button
                    [ngStyle]="{visibility: visible(node) ? 'visible' : 'hidden'}"
                    (click)="openAttachSegmentsModal(node)"
                    style="width: 150px">
              <mat-icon>{{'add'}}</mat-icon>
              Attach segments
            </button>
          </ng-container>
        </div>
      </mat-tree-node>
    </mat-tree>
  `, styleUrls: ['./taxonomy.component.scss'], changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaxonomyComponent implements OnInit, OnDestroy {

  filter$: BehaviorSubject<TaxonomyFilterModel> = new BehaviorSubject<TaxonomyFilterModel>({});

  treeControl = new FlatTreeControl<TaxonomyFlatNode, number>(
    node => node.level,
    node => node.expandable,
    {trackBy: dataNode => dataNode.id});
  treeFlattener = new MatTreeFlattener<Taxonomy, TaxonomyFlatNode, number>(
    (node, level) => (
      {
        ...node,
        expandable: node.children?.length > 0,
        level: node.level,
      }),
    node => node.level,
    node => node.expandable,
    node => node.children);
  buttonsByNodeVisibility: { [nodeId: number]: boolean } = {};
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  private subSink = new SubSink();

  constructor(private facade: TaxonomyFacadeService,
              private dialog: MatDialog,
              private actions: ActionsSubject) {
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  canHaveMoreChildren = (_: number, _nodeData: TaxonomyFlatNode) => (_nodeData.level < taxonomyMaxLevel);

  ngOnInit(): void {
    this.subSink.sink = merge(
      this.actions.pipe(ofType(refreshTaxonomies)),
      this.filter$.asObservable()
    ).subscribe(value => {
      this.facade.loadByFilter(this.filter$.value);
    });
    this.subSink.sink = this.facade.getByFilter()
      .subscribe(taxonomies => {
        this.dataSource.data = taxonomies;
        this.treeControl.expandAll();
      });
  }

  showButtons(node: TaxonomyFlatNode) {
    this.buttonsByNodeVisibility = {...this.buttonsByNodeVisibility, [node.id]: true};
  }

  hideButtons(node: TaxonomyFlatNode) {
    this.buttonsByNodeVisibility = {...this.buttonsByNodeVisibility, [node.id]: false};
  }

  visible(node: TaxonomyFlatNode) {
    if (this.buttonsByNodeVisibility[node.id] === undefined) {
      return false;
    }
    return this.buttonsByNodeVisibility[node.id];
  }

  openAttachSegmentsModal(node: TaxonomyFlatNode) {
    this.dialog.open(SegmentAttachToTaxonomyModalComponent, {
      ...new MatDialogConfig(),
      data: <SegmentAttachToTaxonomyModalData>{taxonomy: node}
    });
  }

  openEditModal(node: TaxonomyFlatNode) {
    this.dialog.open(TaxonomyEditComponent, {
      ...new MatDialogConfig(),
      data: <TaxonomyEditData>{taxonomy: node}
    });
  }

  openCreateModal(node?: TaxonomyFlatNode) {
    this.dialog.open(TaxonomyCreateComponent, {
      ...new MatDialogConfig(),
      data: <TaxonomyEditData>{taxonomy: node}
    });
  }

  onFilterChange($event: TaxonomyFilterModel) {
    this.filter$.next($event);
  }
}
